import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    LinearProgress,
    List,
    ListItem,
    Typography
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axiosInstance from "../AxiosInstance";
import { formatDateForInput, parseWeekStartDate } from "../Utilities/dateUtility";
import { isFieldPast } from "../Utilities/distributionRateUtils";

interface ImportValidationError {
    row: number;
    field: string;
    value: string;
    error: string;
}

interface ImportResult {
    status: "Success" | "ValidationError" | "ProcessingError" | "FileFormatError" | "NoData" | "Locked";
    message: string;
    totalRecords?: number;
    successfulRecords?: number;
    failedRecords?: number;
    validationErrors?: ImportValidationError[];
}

export interface SkuBaseForecastImportDialogProps {
    open: boolean;
    forecastId: number;
    forecastPeriodId: number;
    customerId?: number | null;
    customerName?: string | null;
    onClose: () => void;
    onImported: (result: ImportResult) => Promise<void> | void;
}

const SkuBaseForecastImportDialog: React.FC<SkuBaseForecastImportDialogProps> = ({
    open,
    forecastId,
    forecastPeriodId,
    customerId,
    customerName,
    onClose,
    onImported
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isMultiCustomerMode = customerId == null;
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingWeekOptions, setLoadingWeekOptions] = useState(false);
    const [weekStartDateOptionsLoadFailed, setWeekStartDateOptionsLoadFailed] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [errorText, setErrorText] = useState("");
    const [weekStartDateOptions, setWeekStartDateOptions] = useState<string[]>([]);

    const resetSelectedFile = () => {
        setSelectedFile(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        if (open) {
            resetSelectedFile();
            setLoading(false);
            setLoadingWeekOptions(true);
            setWeekStartDateOptionsLoadFailed(false);
            setIsDragOver(false);
            setResult(null);
            setErrorText("");
            setWeekStartDateOptions([]);

            let active = true;

            void (async () => {
                try {
                    const response = await axiosInstance.get(
                        `/forecasts/${forecastId}/overlay-periods?forecastPeriodId=${forecastPeriodId}`
                    );

                    if (!active) {
                        return;
                    }

                    setWeekStartDateOptions(response.data?.weekStartDateOptions ?? []);
                    setWeekStartDateOptionsLoadFailed(false);
                } catch (fetchError) {
                    if (!active) {
                        return;
                    }

                    console.error("Failed to load forecast weeks for import template:", fetchError);
                    setWeekStartDateOptionsLoadFailed(true);
                    setErrorText("Unable to load forecast weeks for the example template.");
                } finally {
                    if (active) {
                        setLoadingWeekOptions(false);
                    }
                }
            })();

            return () => {
                active = false;
            };
        }
    }, [open, forecastId, forecastPeriodId]);

    const futureWeekHeaders = useMemo(() => {
        return weekStartDateOptions
            .filter((weekStartDate) => !isFieldPast(weekStartDate))
            .map((weekStartDate) => {
                const parsed = parseWeekStartDate(weekStartDate);
                return parsed ? formatDateForInput(parsed) : weekStartDate;
            });
    }, [weekStartDateOptions]);

    const canImport = weekStartDateOptionsLoadFailed || futureWeekHeaders.length > 0;
    const hasExampleWeeks = futureWeekHeaders.length > 0;

    const buildExampleCsv = (weekHeaders: string[], includeCustomerName: boolean) => {
        const baseHeaders = ["SKU", "UnitCost", "RRP", "UnitLogs", ...weekHeaders];
        const headers = includeCustomerName ? ["CustomerName", ...baseHeaders] : baseHeaders;
        const firstRow = includeCustomerName
            ? ["Customer A", "SKU001", "1.250", "2.99", "12.345", ...weekHeaders.map((_, index) => (index === 0 ? "100" : ""))]
            : ["SKU001", "1.250", "2.99", "12.345", ...weekHeaders.map((_, index) => (index === 0 ? "100" : ""))];
        const secondRow = includeCustomerName
            ? ["Customer B", "SKU002", "2.100", "4.99", "8.000", ...weekHeaders.map((_, index) => (index === 1 ? "250" : ""))]
            : ["SKU002", "2.100", "4.99", "8.000", ...weekHeaders.map((_, index) => (index === 1 ? "250" : ""))];

        return [headers.join(","), firstRow.join(","), secondRow.join(",")].join("\n");
    };

    const exampleCsv = useMemo(
        () => buildExampleCsv(futureWeekHeaders, isMultiCustomerMode),
        [futureWeekHeaders, isMultiCustomerMode]
    );
    const exampleFileName = isMultiCustomerMode
        ? "sku_forecast_import_example.csv"
        : "sku_customer_forecast_import_example.csv";
    const dialogTitle = isMultiCustomerMode ? "Import SKU Forecasts" : "Import SKU Customer Forecasts";
    const selectedCustomerName = customerName?.trim();
    const dialogDescription = isMultiCustomerMode
        ? "Upload a CSV for this forecast with one row per SKU. Include customer name on every row."
        : `Upload a CSV for ${selectedCustomerName ?? "this customer"} with one row per SKU.`;

    const handleClose = () => {
        if (loading) {
            return;
        }

        onClose();
    };

    const isCsvFile = (file: File) => {
        const fileName = file.name.toLowerCase();
        return fileName.endsWith(".csv") || file.type === "text/csv" || file.type === "application/vnd.ms-excel";
    };

    const handleFileSelect = (file: File) => {
        if (!isCsvFile(file)) {
            setErrorText("Please select a valid CSV file.");
            resetSelectedFile();
            setResult(null);

            return;
        }

        setErrorText("");
        setSelectedFile(file);
        setResult(null);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDownloadExample = () => {
        if (!canImport) {
            setErrorText("This forecast has no future weeks available for the import template.");
            return;
        }

        const csvBytes = new Blob([exampleCsv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(csvBytes);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", exampleFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleImport = async () => {
        if (!selectedFile) {
            setErrorText("Please select a CSV file.");
            return;
        }

        if (!canImport) {
            setErrorText("This forecast has no future weeks available for import.");
            return;
        }

        setLoading(true);
        setErrorText("");
        setResult(null);

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            const importUrl = isMultiCustomerMode
                ? `/forecasts/${forecastId}/skubaseforecasts/import?forecastPeriodId=${forecastPeriodId}`
                : `/forecasts/${forecastId}/skubaseforecasts/${customerId}/import?forecastPeriodId=${forecastPeriodId}`;

            const response = await axiosInstance.post<ImportResult>(
                importUrl,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (response.data.status === "Success") {
                await onImported(response.data);
                onClose();
                return;
            }

            setResult(response.data);
            resetSelectedFile();
        } catch (err: any) {
            const responseData = err.response?.data;

            if (responseData?.validationErrors) {
                setResult(responseData);
                resetSelectedFile();
            } else {
                setErrorText(
                    typeof responseData?.message === "string"
                        ? responseData.message
                        : typeof responseData === "string"
                            ? responseData
                            : "Import failed."
                );
                resetSelectedFile();
            }
        } finally {
            setLoading(false);
        }
    };

    const validationErrors = result?.validationErrors ?? [];
    const hasValidationErrors = validationErrors.length > 0;
    const resultTone = result?.status === "ProcessingError" ? "error" : "warning";

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" disableEscapeKeyDown={loading}>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ pb: 2 }}>
                    {dialogDescription}
                </DialogContentText>

                {(loading || loadingWeekOptions) && <LinearProgress sx={{ mb: 2 }} />}

                <Alert severity="info" sx={{ mb: 2 }}>
                    UnitCost, RRP and UnitLogs are ignored for existing SKUs. Past, current and blank week columns are ignored. Existing distribution rates are overwritten.
                    {isMultiCustomerMode
                        ? " CustomerName is required on every row."
                        : null}
                </Alert>

                {!loadingWeekOptions && !weekStartDateOptionsLoadFailed && !hasExampleWeeks && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Import is unavailable for this forecast as no future weeks are editable.
                    </Alert>
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Chip label="SKU" size="small" color="primary" />
                    {isMultiCustomerMode && <Chip label="CustomerName" size="small" color="primary" />}
                    <Chip label="UnitCost" size="small" color="primary" />
                    <Chip label="RRP" size="small" color="primary" />
                    <Chip label="UnitLogs" size="small" color="primary" />
                    {hasExampleWeeks ? (
                        <>
                            {futureWeekHeaders.slice(0, 3).map((weekHeader) => (
                                <Chip key={weekHeader} label={weekHeader} size="small" color="secondary" variant="outlined" />
                            ))}
                            {futureWeekHeaders.length > 3 && (
                                <Chip
                                    label={`+${futureWeekHeaders.length - 3} more week columns`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                />
                            )}
                        </>
                    ) : (
                        <Chip label="Future week columns" size="small" color="secondary" variant="outlined" />
                    )}
                </Box>

                <Box
                    sx={{
                        border: isDragOver ? "2px dashed #1976d2" : "2px dashed #ccc",
                        borderRadius: 2,
                        padding: 4,
                        textAlign: "center",
                        mb: 2,
                        cursor: "pointer",
                        backgroundColor: isDragOver ? "action.hover" : "transparent",
                        transition: "border-color 0.2s, background-color 0.2s",
                        "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "action.hover"
                        }
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(event) => {
                        event.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragEnter={(event) => {
                        event.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={(event) => {
                        event.preventDefault();
                        setIsDragOver(false);
                    }}
                    onDrop={(event) => {
                        event.preventDefault();
                        setIsDragOver(false);

                        const files = event.dataTransfer.files;
                        if (files.length > 0) {
                            handleFileSelect(files[0]);
                        }
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        aria-label="sku-customer-forecast-import-file"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                        {selectedFile ? selectedFile.name : "Click to select or drag and drop a CSV file"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Only .csv files are supported
                    </Typography>
                </Box>

                {selectedFile && (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <DescriptionIcon sx={{ mr: 1 }} />
                        <Typography variant="body2">
                            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </Typography>
                    </Box>
                )}

                {!!errorText && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorText("")}>
                        {errorText}
                    </Alert>
                )}

                {result && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            {result.status === "Success" ? (
                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                            ) : (
                                <ErrorIcon color={resultTone === "error" ? "error" : "warning"} sx={{ mr: 1 }} />
                            )}
                            <Typography variant="h6">
                                Import {result.status === "Success" ? "Completed" : "Failed"}
                            </Typography>
                        </Box>

                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {result.message}
                        </Typography>

                        {hasValidationErrors && (
                            <>
                                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                                    <Chip label={`Total lines: ${result.totalRecords ?? 0}`} color="default" />
                                    <Chip
                                        label={`Valid lines: ${result.successfulRecords ?? 0}`}
                                        color="success"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`Invalid lines: ${result.failedRecords ?? validationErrors.length}`}
                                        color="error"
                                    />
                                </Box>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant="body1" gutterBottom component="div">
                                            <strong>No data was imported</strong> because of validation errors.
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            Please fix the errors in your CSV file and upload it again.
                                        </Typography>
                                    </Box>
                                </Alert>
                            </>
                        )}

                        {hasValidationErrors && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Validation Errors
                                </Typography>
                                <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                                    <List dense>
                                        {validationErrors.slice(0, 10).map((error, index) => (
                                            <ListItem key={index} sx={{ px: 0 }}>
                                                <Box>
                                                    <Typography variant="body2" component="div">
                                                        Row {error.row}: {error.field}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary" component="div">
                                                        Value: "{error.value}" - {error.error}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        ))}
                                        {validationErrors.length > 10 && (
                                            <ListItem sx={{ px: 0 }}>
                                                <Typography variant="body2" color="textSecondary" component="div">
                                                    ... and {validationErrors.length - 10} more errors
                                                </Typography>
                                            </ListItem>
                                        )}
                                    </List>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Button onClick={handleDownloadExample} startIcon={<DescriptionIcon />} disabled={loading || loadingWeekOptions || !hasExampleWeeks}>
                    Download Example
                </Button>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleImport} disabled={loading || loadingWeekOptions || !selectedFile || !canImport}>
                        {loading ? "Importing..." : "Import"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default SkuBaseForecastImportDialog;
